import { useEffect, useMemo, useRef, useState } from 'react';
import { Maximize2, Monitor, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { THEME_PREVIEW_FRAME_PATH } from '@/lib/theme-presets';

/** The admin's unsaved tweaks, layered over the preset in the preview. */
export interface ThemePreviewOverrides {
  headingFont?: string;
  bodyFont?: string;
  buttonStyle?: string;
  primaryColor?: string | null;
  buttonTextColor?: string | null;
}

/** Width the real site is laid out at inside a scaled preview (a normal laptop). */
const DESKTOP_WIDTH = 1280;
/** Height of the desktop viewport shown on a card: the first screen of the site. */
const DESKTOP_HEIGHT = 820;
const MOBILE_WIDTH = 390;

/** URL of the preview route for a preset (+ optional unsaved overrides). Never saves anything. */
function themePreviewUrl(presetId: string, overrides?: ThemePreviewOverrides): string {
  const q = new URLSearchParams({ preset: presetId });
  if (overrides?.headingFont) q.set('hf', overrides.headingFont);
  if (overrides?.bodyFont) q.set('bf', overrides.bodyFont);
  if (overrides?.buttonStyle) q.set('bs', overrides.buttonStyle);
  if (overrides?.primaryColor) q.set('pc', overrides.primaryColor);
  if (overrides?.buttonTextColor) q.set('bt', overrides.buttonTextColor);
  return `${THEME_PREVIEW_FRAME_PATH}?${q.toString()}`;
}

/** Debounce a value so a colour picker drag doesn't reload the iframe on every tick. */
function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), ms);
    return () => window.clearTimeout(id);
  }, [value, ms]);
  return debounced;
}

interface ThemeLivePreviewProps {
  presetId: string;
  presetName: string;
  overrides?: ThemePreviewOverrides;
  className?: string;
  /** Show a "Preview full size" button over the preview. */
  expandable?: boolean;
}

/**
 * The real site, scaled down: an iframe of `/admin/theme-preview-frame` laid out
 * at desktop width and scaled to the card. It mounts only once it scrolls into
 * view, ignores pointer input, and is the exact same code that renders the
 * live homepage, so images, logo, reviews and layout always match.
 */
export function ThemeLivePreview({ presetId, presetName, overrides, className, expandable = true }: ThemeLivePreviewProps) {
  const boxRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [width, setWidth] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [open, setOpen] = useState(false);
  const debouncedOverrides = useDebounced(overrides, 450);
  const src = useMemo(() => themePreviewUrl(presetId, debouncedOverrides), [presetId, debouncedOverrides]);

  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const resize = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    resize.observe(el);
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          io.disconnect();
        }
      },
      { rootMargin: '200px' },
    );
    io.observe(el);
    return () => {
      resize.disconnect();
      io.disconnect();
    };
  }, []);

  useEffect(() => setLoaded(false), [src]);

  const scale = width > 0 ? width / DESKTOP_WIDTH : 0;

  return (
    <div className={cn('relative', className)}>
      <div
        ref={boxRef}
        data-testid={`theme-live-preview-${presetId}`}
        className="relative w-full overflow-hidden rounded-lg border border-border bg-muted"
        style={{ aspectRatio: `${DESKTOP_WIDTH} / ${DESKTOP_HEIGHT}` }}
      >
        {!loaded && <div className="absolute inset-0 animate-pulse bg-muted" aria-hidden />}
        {visible && scale > 0 && (
          <iframe
            key={presetId}
            src={src}
            title={`${presetName} preview`}
            loading="lazy"
            tabIndex={-1}
            aria-hidden="true"
            onLoad={() => setLoaded(true)}
            className={cn('pointer-events-none absolute left-0 top-0 origin-top-left border-0 bg-background transition-opacity duration-300', loaded ? 'opacity-100' : 'opacity-0')}
            style={{ width: DESKTOP_WIDTH, height: DESKTOP_HEIGHT, transform: `scale(${scale})` }}
          />
        )}
      </div>
      {expandable && (
        <>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={() => setOpen(true)}
            className="absolute right-2 top-2 h-7 gap-1.5 px-2 text-[11.5px] opacity-90 shadow-sm hover:opacity-100"
            aria-label={`Preview ${presetName} full size`}
          >
            <Maximize2 className="h-3 w-3" />
            Full size
          </Button>
          {open && <FullSizePreview presetId={presetId} presetName={presetName} overrides={overrides} open={open} onOpenChange={setOpen} />}
        </>
      )}
    </div>
  );
}

function FullSizePreview({ presetId, presetName, overrides, open, onOpenChange }: {
  presetId: string;
  presetName: string;
  overrides?: ThemePreviewOverrides;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [device, setDevice] = useState<'desktop' | 'mobile'>('desktop');
  const [stageEl, setStageEl] = useState<HTMLDivElement | null>(null);
  const [stage, setStage] = useState({ w: 0, h: 0 });
  const src = useMemo(() => themePreviewUrl(presetId, overrides), [presetId, overrides]);

  useEffect(() => {
    if (!stageEl) return;
    const ro = new ResizeObserver(([entry]) => setStage({ w: entry.contentRect.width, h: entry.contentRect.height }));
    ro.observe(stageEl);
    return () => ro.disconnect();
  }, [stageEl]);

  const frameW = device === 'desktop' ? DESKTOP_WIDTH : MOBILE_WIDTH;
  const scale = stage.w > 0 ? Math.min(1, stage.w / frameW) : 0;
  const frameH = stage.h > 0 && scale > 0 ? stage.h / scale : device === 'desktop' ? DESKTOP_HEIGHT : 780;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[92vh] max-w-[min(96vw,1400px)] flex-col gap-3 p-4 sm:p-5">
        <DialogHeader className="flex-row items-center justify-between gap-3 space-y-0 pr-8">
          <div className="min-w-0">
            <DialogTitle className="truncate">{presetName}</DialogTitle>
            <DialogDescription className="sr-only">Full-size preview of your homepage in this theme. Nothing is saved.</DialogDescription>
          </div>
          <div className="flex shrink-0 gap-1 rounded-lg border border-border p-0.5" role="group" aria-label="Preview size">
            <Button type="button" size="sm" variant={device === 'desktop' ? 'secondary' : 'ghost'} className="h-7 gap-1.5 px-2.5" onClick={() => setDevice('desktop')} aria-pressed={device === 'desktop'}>
              <Monitor className="h-3.5 w-3.5" /> Desktop
            </Button>
            <Button type="button" size="sm" variant={device === 'mobile' ? 'secondary' : 'ghost'} className="h-7 gap-1.5 px-2.5" onClick={() => setDevice('mobile')} aria-pressed={device === 'mobile'}>
              <Smartphone className="h-3.5 w-3.5" /> Mobile
            </Button>
          </div>
        </DialogHeader>
        <div ref={setStageEl} className="relative flex min-h-0 flex-1 justify-center overflow-hidden rounded-lg border border-border bg-muted">
          {scale > 0 && (
            <div style={{ width: frameW * scale, height: frameH * scale }} className="shrink-0">
              <iframe
                src={src}
                title={`${presetName} full-size preview`}
                data-testid="theme-fullsize-iframe"
                className="origin-top-left border-0 bg-background"
                style={{ width: frameW, height: frameH, transform: `scale(${scale})` }}
              />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
